import { z } from 'zod';
/**
 * Контракт роута `/api/system-settings` — общесистемные флаги `app_settings`,
 * у которых до сих пор не было НИ ОДНОГО экрана: включать их приходилось
 * INSERT'ом в прод-базу.
 *
 * Один роут и один контракт НА ОБА флага, а не по роуту на флаг. Это то же
 * правило, что записано у {@link InboxSortByActivitySetting} в
 * `llm-settings.ts`: настройки растут по одной, и «завести свой роут, своё
 * право и свой экран» на каждую — ровно тот путь, которым порог кита оказался
 * спрятан в третьем уровне вложенности чужого раздела.
 *
 * Почему тогда роутов всё-таки три, а не один. Границей служит ПРАВО НА
 * ЗАПИСЬ, а не тема: `/api/llm-settings` (`llm.manage`),
 * `/api/billing-settings` (`billing_settings.manage`) и этот роут существуют
 * отдельно, потому что тиры записи у них разные и слить их значило бы либо
 * поднять всем тир до самого строгого, либо опустить до самого мягкого. Экран
 * при этом ОДИН — `admin-module-settings` собирает все три источника на одной
 * странице, и это та поверхность, которую видит оператор. Новый булев флаг
 * добавляется ПОЛЕМ СЮДА (или в один из двух соседних контрактов по тиру
 * записи), а не четвёртым роутом.
 *
 * Форма значения в `app_settings` у обоих флагов НЕ МЕНЯЕТСЯ: их уже читают
 * `loadRiskFuzzyCorrelationEnabled` (`packages/db/src/risk-alert-store.ts`) и
 * `readFollowupPingFlag` (`apps/telegram-bot/src/followup-ping.ts`), и панель
 * обязана писать ровно то, что эти читатели понимают.
 */
/**
 * WRITE-граница флага `risk.fuzzy_correlation` — эвристической (4-й ключ)
 * склейки алертов платёжного риска в один кейс.
 *
 * КЛЮЧ настройки живёт в `@wit-support/db` (`RISK_FUZZY_CORRELATION_KEY`) и
 * сюда не копируется — по той же причине, что и ключ порога кита: его читает
 * сервис синхронизации и бэкфилл, и второй экземпляр константы означал бы, что
 * переименование оставит панель редактировать строку, которую никто не читает.
 *
 * `.strict()` — на записи лишнее или опечатанное поле должно быть отвергнуто,
 * а не сохранено молча. Форма `{enabled: boolean}` совпадает с той, что
 * структурно (без Zod, `packages/db` не зависит от `zod`) проверяет
 * `loadRiskFuzzyCorrelationEnabled`.
 */
export const RiskFuzzyCorrelationSetting = z.object({ enabled: z.boolean() }).strict();
/**
 * READ-граница `risk.fuzzy_correlation`: дополнительные поля от писателя
 * поновее проходят насквозь, не-булев или отсутствующий `enabled` — провал
 * разбора и fail-safe ВЫКЛЮЧЕНО.
 *
 * Направление fail-safe здесь важнее обычного и совпадает с
 * `loadRiskFuzzyCorrelationEnabled`: ложная склейка двух РАЗНЫХ инцидентов
 * дороже, чем два отдельных кейса, поэтому испорченное значение обязано читаться
 * как «выключено», а не как «включено».
 */
export const RiskFuzzyCorrelationStoredSetting = z.object({ enabled: z.boolean() }).passthrough();
/**
 * READ-граница ответа `GET /api/system-settings`. Объявлена независимо от
 * запроса (тот частичный), потому что ответ ВСЕГДА несёт действующее значение
 * каждого флага — ровно как {@link LlmSettings}.
 */
export const SystemSettings = z.object({
    /** `risk.fuzzy_correlation` — эвристическая склейка алертов. */
    fuzzyRiskCorrelation: z.boolean(),
    /** `notifications.telegram_followup_ping` — напоминание в Telegram об ответе клиента. */
    telegramFollowupPing: z.boolean(),
});
/**
 * WRITE-граница `PATCH /api/system-settings`.
 *
 * ВСЕ поля необязательны, и это несущая деталь, а не удобство: у двух флагов
 * РАЗНЫЕ тиры записи (риск — владелец, уведомления — супервайзер+), и роут
 * проверяет право отдельно по каждому присутствующему полю. Обязательное поле
 * означало бы, что супервайзер не может тронуть уведомления, не прислав заодно
 * значение флага рисков, которое ему менять нельзя.
 *
 * Частичность заодно спасает раскатку: закешированный старый бандл присылает
 * только своё поле. `.strict()` при этом отвергает опечатки — `{fuzzyRisk: true}`
 * иначе вернул бы 200 и не изменил ничего.
 */
export const UpdateSystemSettingsRequest = z
    .object({
    fuzzyRiskCorrelation: z.boolean().optional(),
    telegramFollowupPing: z.boolean().optional(),
})
    .strict();
//# sourceMappingURL=system-settings.js.map